import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { RouterProvider } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import theme from 'config/theme';
import { ENV_CONFIG } from 'config/environment';
import { AUTH0_PROVIDER_SCOPE, AUTH0_USE_REFRESH_TOKENS } from 'config/environment/auth0-constants';
import FontStyles from 'config/theme/fontStyles';
import store from 'store/create-store';
import { router } from 'navigation/router';
import reportWebVitals from './reportWebVitals';
import { datadogLogs } from '@datadog/browser-logs';
import mixpanel from 'mixpanel-browser';
import { mixpanelClient } from 'helpers/mixpanel';
import { Statsig as StatsigProvider } from 'providers/Statsig';
import { datadogRum } from '@datadog/browser-rum';

const { DATADOG_LOGS_INIT, DATADOG_RUM_INIT } = ENV_CONFIG();
datadogLogs.init(DATADOG_LOGS_INIT);
if (DATADOG_RUM_INIT) datadogRum.init(DATADOG_RUM_INIT);

// Near entry of your product, init Mixpanel
mixpanel.init(process.env.REACT_APP_MIXPANEL_PROJECT_TOKEN as string, {
  persistence: 'localStorage',
});

import 'assets/scss/site.scss';
import 'react-toastify/dist/ReactToastify.css';
import 'react-spring-bottom-sheet/dist/style.css';
import 'rc-slider/assets/index.css';

/** Must stay at app root: the auth mirror needs every route, and `useAuth` only covers ~10. */
const AnalyticsIdentify = () => {
  const { user, isAuthenticated, isLoading } = useAuth0();
  useEffect(() => {
    // Auth0 reports false while restoring a cached session; don't record that as anonymous.
    if (isLoading) return;
    mixpanelClient.setAuthState(isAuthenticated);
    if (isAuthenticated && user?.sub) {
      mixpanelClient.identifyWithMergeEvent(user.sub);
      datadogRum.setUser({ id: user.sub });
      return;
    }
    datadogRum.clearUser();
  }, [isLoading, isAuthenticated, user?.sub]);
  return null;
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <Auth0Provider
      domain={ENV_CONFIG().AUTH.DOMAIN}
      cacheLocation="localstorage"
      clientId={ENV_CONFIG().AUTH.CLIENT_ID}
      useRefreshTokens={AUTH0_USE_REFRESH_TOKENS}
      useRefreshTokensFallback={true}
      authorizationParams={{
        redirect_uri: `${ENV_CONFIG().SITE_URL}/profile`,
        audience: `https://${ENV_CONFIG().AUTH.DOMAIN}/api/v2/`,
        scope: AUTH0_PROVIDER_SCOPE,
      }}
    >
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <StatsigProvider>
            <AnalyticsIdentify />
            <FontStyles />
            <RouterProvider router={router} />
          </StatsigProvider>
        </ThemeProvider>
      </Provider>
    </Auth0Provider>
  </React.StrictMode>
);

reportWebVitals();
